import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('dse-learn-token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('dse-learn-token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchUser = useCallback(async (authToken) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on 401 unauthorized, not network errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (identifier, password, turnstileToken = null) => {
    const payload = { identifier, password };
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const response = await axios.post(`${API}/auth/login`, payload);

    // 2FA-enabled accounts: backend returns { requires_2fa: true, pending_token }
    if (response.data?.requires_2fa) {
      return { requires_2fa: true, pending_token: response.data.pending_token };
    }

    const { access_token } = response.data;
    localStorage.setItem('dse-learn-token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);

    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      return userResponse.data;
    } catch (error) {
      console.error('Failed to fetch user after login:', error);
      return { name: 'User' };
    }
  };

  const verifyLogin2FA = async (pending_token, code) => {
    const response = await axios.post(`${API}/auth/login/verify-2fa`, { pending_token, code });
    const { access_token } = response.data;
    localStorage.setItem('dse-learn-token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      return userResponse.data;
    } catch (error) {
      console.error('Failed to fetch user after 2FA verify:', error);
      return { name: 'User' };
    }
  };

  const toggle2FA = async (enable) => {
    const response = await axios.post(`${API}/auth/2fa/toggle`, { enable });
    return response.data;
  };

  const register = async (data) => {
    // Transform email/phone to identifier format that backend expects
    const payload = {
      name: data.name,
      identifier: data.email || data.phone,
      password: data.password
    };
    if (data.turnstileToken) payload.turnstile_token = data.turnstileToken;
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token } = response.data;
    
    localStorage.setItem('dse-learn-token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    
    // Fetch user data with new token
    try {
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      return userResponse.data;
    } catch (error) {
      console.error('Failed to fetch user after register:', error);
      return { name: data.name };
    }
  };

  const updateUser = (updatedData) => {
    setUser(prev => ({ ...prev, ...updatedData }));
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    verifyLogin2FA,
    toggle2FA,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
